/**
 * Serviço de Gerenciamento de Comunicações
 * Filtros por data de publicação e outras funcionalidades
 */

import { FirebaseService } from './firebaseService';
import cacheService from './cacheService';

class ComunicacaoService {
  
  /**
   * Buscar comunicações por data de publicação
   */
  static async getComunicacoesPorData(userId, dataInicio, dataFim) {
    try {
      console.log('📅 Buscando comunicações por data:', { dataInicio, dataFim });
      
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
      
      // Filtrar por data de publicação
      const comunicacoesFiltradas = comunicacoes.filter(com => {
        if (!com.data_disponibilizacao) {
          console.log('⚠️ Comunicação sem data_disponibilizacao:', com.id);
          return false;
        }

        const dataPublicacao = new Date(com.data_disponibilizacao);
        const inicio = new Date(dataInicio);
        const fim = new Date(dataFim);

        // Ajustar para incluir o dia inteiro
        inicio.setHours(0, 0, 0, 0);
        fim.setHours(23, 59, 59, 999);

        const dentroDoRange = dataPublicacao >= inicio && dataPublicacao <= fim;

        console.log('📅 Verificando comunicação:', {
          id: com.id,
          dataPublicacao: com.data_disponibilizacao,
          dataPublicacaoFormatada: dataPublicacao.toLocaleDateString('pt-BR'),
          inicio: inicio.toLocaleDateString('pt-BR'),
          fim: fim.toLocaleDateString('pt-BR'),
          dentroDoRange
        });

        return dentroDoRange;
      });
      
      // Ordenar por data mais recente primeiro
      comunicacoesFiltradas.sort((a, b) => 
        new Date(b.data_disponibilizacao) - new Date(a.data_disponibilizacao)
      );
      
      console.log(`✅ ${comunicacoesFiltradas.length} comunicações encontradas`);
      return comunicacoesFiltradas;
      
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações por data:', error);
      throw error;
    }
  }
  
  /**
   * Buscar comunicações de hoje
   */
  static async getComunicacoesHoje(userId) {
    const hoje = new Date().toISOString().split('T')[0];
    console.log('📅 Buscando comunicações de HOJE:', hoje);
    return this.getComunicacoesPorData(userId, hoje, hoje);
  }
  
  /**
   * Buscar comunicações de ontem
   */
  static async getComunicacoesOntem(userId) {
    const ontem = new Date();
    ontem.setDate(ontem.getDate() - 1);
    const dataOntem = ontem.toISOString().split('T')[0];
    return this.getComunicacoesPorData(userId, dataOntem, dataOntem);
  }
  
  /**
   * Buscar comunicações da última semana
   */
  static async getComunicacoesSemana(userId) {
    const hoje = new Date();
    const semanaAtras = new Date();
    semanaAtras.setDate(hoje.getDate() - 7);
    
    return this.getComunicacoesPorData(
      userId, 
      semanaAtras.toISOString().split('T')[0],
      hoje.toISOString().split('T')[0]
    );
  }
  
  /**
   * Buscar comunicações do último mês
   */
  static async getComunicacoesMes(userId) {
    const hoje = new Date();
    const mesAtras = new Date();
    mesAtras.setMonth(hoje.getMonth() - 1);
    
    return this.getComunicacoesPorData(
      userId, 
      mesAtras.toISOString().split('T')[0],
      hoje.toISOString().split('T')[0]
    );
  }
  
  /**
   * Obter estatísticas de comunicações por período
   */
  static async getEstatisticasComunicacoes(userId) {
    try {
      const [hoje, ontem, semana, mes] = await Promise.all([
        this.getComunicacoesHoje(userId),
        this.getComunicacoesOntem(userId),
        this.getComunicacoesSemana(userId),
        this.getComunicacoesMes(userId)
      ]);
      
      return {
        hoje: hoje.length,
        ontem: ontem.length,
        semana: semana.length,
        mes: mes.length,
        comunicacoesHoje: hoje,
        comunicacoesOntem: ontem
      };
      
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas:', error);
      return {
        hoje: 0,
        ontem: 0,
        semana: 0,
        mes: 0,
        comunicacoesHoje: [],
        comunicacoesOntem: []
      };
    }
  }
  
  /**
   * Re-processar comunicações existentes com nova lógica
   */
  static async reprocessarComunicacoes(userId) {
    try {
      console.log('🔄 Re-processando comunicações existentes...');

      // Buscar todas as comunicações do usuário
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);

      console.log(`📄 Encontradas ${comunicacoes.length} comunicações para re-processar`);

      if (comunicacoes.length === 0) {
        console.log('⚠️ Nenhuma comunicação encontrada para re-processar');
        return [];
      }

      // Re-formatar cada uma com a nova lógica
      console.log('🔧 Aplicando nova lógica de formatação...');
      const comunicacoesReprocessadas = comunicacoes.map(com => {
        console.log(`📄 Re-processando: ${com.id}`);
        return this.formatarComunicacao(com);
      });

      console.log('✅ Re-processamento concluído');
      console.log(`📊 ${comunicacoesReprocessadas.length} comunicações re-processadas`);

      return comunicacoesReprocessadas;

    } catch (error) {
      console.error('❌ Erro no re-processamento:', error);
      throw error;
    }
  }

  /**
   * Formatar comunicação para exibição
   */
  static formatarComunicacao(comunicacao) {
    // Debug completo dos dados disponíveis
    if (comunicacao.id && comunicacao.id.includes('demo')) {
      // Para dados demo, já estão formatados
      return {
        id: comunicacao.id,
        numeroProcesso: comunicacao.numeroProcesso || 'N/A',
        tribunal: comunicacao.tribunal || 'N/A',
        dataPublicacao: comunicacao.dataPublicacao || new Date().toISOString().split('T')[0],
        tipoAto: comunicacao.tipoAto || 'Comunicação',
        conteudo: comunicacao.conteudo || 'Conteúdo não disponível',
        processada: comunicacao.processada || false,
        prazoGerado: comunicacao.prazoGerado || false,
        dataProcessamento: comunicacao.dataProcessamento || null
      };
    }

    // Log de debug para dados reais do Firebase
    console.log('🔧 Formatando comunicação - dados disponíveis:', {
      id: comunicacao.id,
      campos: Object.keys(comunicacao),
      // Datas disponíveis
      datas: Object.keys(comunicacao).filter(key => key.includes('data')),
      // Campos de texto
      textos: Object.keys(comunicacao).filter(key => 
        key.includes('texto') || key.includes('conteudo') || key.includes('comunicacao')
      ),
      // Campos de processo
      processos: Object.keys(comunicacao).filter(key => 
        key.includes('processo') || key.includes('numero')
      ),
      // Campos de tribunal
      tribunais: Object.keys(comunicacao).filter(key => 
        key.includes('tribunal') || key.includes('sigla') || key.includes('orgao')
      )
    });

    // Extrair dados com múltiplas tentativas
    const id = comunicacao.id || `comm_${Date.now()}`;
    
    // Extrair número do processo (múltiplas variações)
    const numeroProcesso = comunicacao.numeroProcesso || 
                          comunicacao.numeroprocessocommascara || 
                          comunicacao.numeroProcessoComMascara ||
                          comunicacao.numero_processo ||
                          comunicacao.npu ||
                          'N/A';

    // Extrair tribunal (múltiplas variações)
    const tribunal = comunicacao.tribunal ||
                    comunicacao.siglaTribunal ||
                    comunicacao.sigla_tribunal ||
                    comunicacao.orgao ||
                    comunicacao.nomeOrgao ||
                    comunicacao.tribunal_sigla ||
                    'N/A';

    // Extrair data de publicação (preferir data_disponibilizacao)
    const dataPublicacao = comunicacao.dataPublicacao ||
                          comunicacao.data_publicacao ||
                          comunicacao.data_disponibilizacao ||
                          comunicacao.data_intimacao ||
                          comunicacao.data_elaboracao ||
                          new Date().toISOString().split('T')[0];

    // Extrair tipo de ato
    const tipoAto = comunicacao.tipoAto ||
                   comunicacao.tipo_ato ||
                   comunicacao.aiAnalysis?.tipoAto ||
                   comunicacao.tipoDocumento ||
                   comunicacao.classe ||
                   'Comunicação';

    // Extrair conteúdo (PRIORIDADE MÁXIMA)
    const conteudo = comunicacao.conteudo ||
                    comunicacao.texto ||
                    comunicacao.textoCompleto ||
                    comunicacao.texto_comunicacao ||
                    comunicacao.textoConteudo ||
                    comunicacao.texto_completo ||
                    comunicacao.conteudoTexto ||
                    comunicacao.textoInteiroComQuebrasDeLinha ||
                    comunicacao.textoInteiroSemQuebrasDeLinha ||
                    comunicacao.conteudoCompleto ||
                    comunicacao.textoConteudoCompleto ||
                    comunicacao.documentoTexto ||
                    comunicacao.textoDocumento ||
                    // Verificar outros campos que podem conter texto
                    (function() {
                      // Buscar qualquer campo que tenha texto longo (provavelmente conteúdo)
                      for (const [key, value] of Object.entries(comunicacao)) {
                        if (typeof value === 'string' && value.length > 50 && 
                            !key.includes('id') && 
                            !key.includes('numero') && 
                            !key.includes('data') &&
                            !key.includes('sigla') &&
                            !key.includes('tribunal')) {
                          console.log(`📝 Campo com texto longo encontrado: ${key} (${value.length} chars)`);
                          return value;
                        }
                      }
                      return null;
                    })() ||
                    'Conteúdo da comunicação não disponível para visualização.';

    // Log do conteúdo encontrado
    console.log('📝 Conteúdo extraído:', {
      id: id,
      conteudo: conteudo.substring(0, 100) + (conteudo.length > 100 ? '...' : ''),
      tamanho: conteudo.length
    });

    // Verificar se foi processada por IA
    const processada = Boolean(comunicacao.processada || comunicacao.aiAnalysis);
    const prazoGerado = Boolean(comunicacao.prazoGerado || comunicacao.aiAnalysis?.temPrazo);

    const resultado = {
      id,
      numeroProcesso,
      tribunal,
      dataPublicacao: typeof dataPublicacao === 'string' ? 
        dataPublicacao.split('T')[0] : 
        new Date(dataPublicacao).toISOString().split('T')[0],
      tipoAto,
      conteudo,
      processada,
      prazoGerado,
      dataProcessamento: comunicacao.dataProcessamento || comunicacao.processadaEm || null,
      
      // Dados originais para debug
      dadosOriginais: comunicacao
    };

    console.log('✅ Comunicação formatada:', {
      id: resultado.id,
      numeroProcesso: resultado.numeroProcesso,
      tribunal: resultado.tribunal,
      tipoAto: resultado.tipoAto,
      dataPublicacao: resultado.dataPublicacao,
      conteudoTamanho: resultado.conteudo.length,
      processada: resultado.processada
    });

    return resultado;
  }
  
  /**
   * Marcar comunicação como lida
   */
  static async marcarComoLida(userId, comunicacaoId) {
    try {
      await FirebaseService.updateComunicacao(userId, comunicacaoId, {
        isRead: true,
        dataLeitura: new Date().toISOString()
      });
      
      // Invalidar cache relacionado
      cacheService.delete(`comunicacoes_${userId}`);
      
      console.log(`✅ Comunicação ${comunicacaoId} marcada como lida`);
    } catch (error) {
      console.error('❌ Erro ao marcar comunicação como lida:', error);
      throw error;
    }
  }
  
  /**
   * Marcar múltiplas comunicações como lidas
   */
  static async marcarMultiplasComoLidas(userId, comunicacaoIds) {
    try {
      const promises = comunicacaoIds.map(id => 
        this.marcarComoLida(userId, id)
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('❌ Erro ao marcar múltiplas comunicações como lidas:', error);
      throw error;
    }
  }
  
  /**
   * Buscar comunicações não lidas
   */
  static async getComunicacoesNaoLidas(userId) {
    try {
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
      return comunicacoes.filter(com => !com.isRead);
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações não lidas:', error);
      return [];
    }
  }
  
  /**
   * Buscar comunicações por tipo de ato
   */
  static async getComunicacoesPorTipo(userId, tipoAto) {
    try {
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
      return comunicacoes.filter(com => 
        com.aiAnalysis?.tipoAto?.toLowerCase() === tipoAto.toLowerCase()
      );
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações por tipo:', error);
      return [];
    }
  }
  
  /**
   * Buscar comunicações por tribunal
   */
  static async getComunicacoesPorTribunal(userId, tribunal) {
    try {
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
      return comunicacoes.filter(com => 
        com.siglaTribunal?.toLowerCase() === tribunal.toLowerCase()
      );
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações por tribunal:', error);
      return [];
    }
  }
  
  /**
   * Buscar comunicações por número de processo
   */
  static async getComunicacoesPorProcesso(userId, numeroProcesso) {
    try {
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
      const numeroLimpo = numeroProcesso.replace(/[^0-9]/g, '');
      
      return comunicacoes.filter(com => {
        const numeroComLimpo = (com.numeroProcesso || com.numeroprocessocommascara || '').replace(/[^0-9]/g, '');
        return numeroComLimpo.includes(numeroLimpo);
      });
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações por processo:', error);
      return [];
    }
  }

  /**
   * Buscar todas as comunicações do usuário (com cache)
   */
  static async getUserComunicacoes(userId) {
    try {
      const cacheKey = `comunicacoes_${userId}`;
      
      // Usar cache inteligente
      return await cacheService.cached(cacheKey, async () => {
        console.log('📄 Buscando todas as comunicações do usuário:', userId);
        
        const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
        
        // Formatar comunicações para exibição
        const comunicacoesFormatadas = comunicacoes.map(com => 
          this.formatarComunicacao(com)
        );
        
        // Ordenar por data mais recente primeiro
        comunicacoesFormatadas.sort((a, b) => 
          new Date(b.dataPublicacao || b.dataDisponibilizacao) - new Date(a.dataPublicacao || a.dataDisponibilizacao)
        );
        
        console.log(`✅ ${comunicacoesFormatadas.length} comunicações carregadas`);
        return comunicacoesFormatadas;
      });
      
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações do usuário:', error);
      return [];
    }
  }

  /**
   * 🤖 PROCESSAR AUTOMATICAMENTE comunicações não processadas
   * Transforma comunicações em prazos automaticamente
   */
  static async processarComunicacoesAutomaticamente(userId) {
    try {
      console.log('🤖 Iniciando processamento automático de comunicações...');

      // Importar serviço de IA diretamente
      const { default: AIAnalysisService } = await import('./aiAnalysisService.js');

      // 1. Buscar TODAS as comunicações que precisam ser processadas
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId);
      const comunicacoesNaoProcessadas = comunicacoes.filter(com => 
        !com.processada || // Não foi processada ainda
        !com.aiAnalysis || // Não tem análise de IA
        com.aiAnalysis?.prazos?.length === 0 // Tem análise mas sem prazos
      );

      if (comunicacoesNaoProcessadas.length === 0) {
        console.log('✅ Todas as comunicações já foram processadas');
        return { processadas: 0, total: comunicacoes.length, totalComunicacoes: comunicacoes.length };
      }

      console.log(`🔄 Processando ${comunicacoesNaoProcessadas.length} comunicações com IA...`);

      let processadasComSucesso = 0;
      let prazosDetectados = 0;

      // 2. Processar cada comunicação (máximo 10 por vez para não travar)
      const limite = Math.min(comunicacoesNaoProcessadas.length, 10);
      
      for (let i = 0; i < limite; i++) {
        const comunicacao = comunicacoesNaoProcessadas[i];
        
        try {
          console.log(`🤖 [${i+1}/${limite}] Processando comunicação: ${comunicacao.id?.substring(0, 20)}...`);

          // Analisar comunicação com IA
          const analysis = await AIAnalysisService.analyzeComunicacao(comunicacao);

          // Contar prazos detectados
          const prazosDaComunicacao = analysis.prazos?.length || 0;
          prazosDetectados += prazosDaComunicacao;

          // Atualizar comunicação com análise
          await FirebaseService.updateComunicacao(userId, comunicacao.id, {
            aiAnalysis: analysis,
            processada: true,
            processadaEm: new Date().toISOString(),
            temPrazo: analysis.temPrazo,
            urgencia: analysis.urgenciaGeral
          });

          processadasComSucesso++;
          console.log(`✅ Comunicação processada: ${prazosDaComunicacao} prazo(s) detectado(s)`);

          // Delay para evitar rate limiting da OpenAI
          await new Promise(resolve => setTimeout(resolve, 1500));

        } catch (error) {
          console.error(`❌ Erro ao processar comunicação ${comunicacao.id}:`, error.message);
          // Continuar processando as outras mesmo se uma falhar
        }
      }

      const restantes = comunicacoesNaoProcessadas.length - limite;
      
      console.log(`🎉 Processamento concluído!`);
      console.log(`📊 ${processadasComSucesso}/${limite} comunicações processadas`);
      console.log(`📅 ${prazosDetectados} prazos detectados`);
      if (restantes > 0) {
        console.log(`⏳ Ainda restam ${restantes} comunicações para processar`);
      }

      return {
        processadas: processadasComSucesso,
        total: comunicacoesNaoProcessadas.length,
        totalComunicacoes: comunicacoes.length,
        prazosGerados: prazosDetectados,
        restantes: restantes
      };

    } catch (error) {
      console.error('❌ Erro no processamento automático:', error);
      throw error;
    }
  }
}

export default ComunicacaoService;
